'use client';

import type {
  Overlay,
  OverlayFigure,
  OverlayTemplate,
  Point,
  Coordinate,
  Bounding,
  Chart,
  OverlayPerformEventParams,
  OverlayEvent,
} from 'klinecharts';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const CUSTOM_FIB_OVERLAY_NAME = 'customFibonacci';
export const LEGACY_CUSTOM_FIB_OVERLAY_NAMES = ['fibonacciLine'];

export const DEFAULT_CUSTOM_FIB_LEVEL_VALUES: number[] = [
  0,
  0.5,
  0.62,
  0.705,
  0.79,
  1,
];

const DEFAULT_GOLDEN_ZONE = {
  from: 0.5,
  to: 0.618,
  visible: true,
};

const LABEL_FONT_SIZE_PX = 11;
const LABEL_FONT_WEIGHT = 600;
const LABEL_FONT_FAMILY = 'ui-sans-serif, system-ui, sans-serif';
const LABEL_HORIZONTAL_PADDING_PX = 7;
const LABEL_VERTICAL_PADDING_PX = 4;
const LABEL_HEIGHT_PX = 20;
const LABEL_EDGE_PADDING_PX = 8;
const LABEL_END_GAP_PX = 6;
const LABEL_MIN_GAP_DESKTOP_PX = 12;
const LABEL_MIN_GAP_MOBILE_PX = 18;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FibLevel {
  id: string;
  value: number;
  visible: boolean;
  color?: string;
}

export interface FibExtendData {
  levels: FibLevel[];
  reverse: boolean;
  extendLeft: boolean;
  extendRight: boolean;
  trendLine: { visible: boolean; color?: string; size?: number; style?: 'solid' | 'dashed' | 'dotted' };
  backgrounds: { visible: boolean; alpha: number };
  labels: { showPrices: boolean; showLevels: boolean; levelsFormat: 'values' | 'percents'; position: 'left' | 'center' | 'right'; align: 'top' | 'middle' | 'bottom' };
  fibBasedOnLogScale: boolean;
  goldenZone: { from: number; to: number; visible: boolean };
  lytickStyleVariant: string;
}

export interface FibSnapshot {
  name?: string;
  extendData?: FibExtendData | unknown;
  [key: string]: unknown;
}

interface Palette {
  accent: string;
  accentGhost: string;
  baseTrend: string;
  lineColor: string;
  levelLine: string;
  levelLineStrong: string;
  extensionLine: string;
  zoneFill: string;
  zoneBorder: string;
  textPrimary: string;
  textSecondary: string;
  labelBackground: string;
  labelBorder: string;
  pointBorderColor: string;
  activeBorderColor: string;
  activeColor: string;
}

interface LabelEntry {
  level: FibLevel;
  price: number;
  y: number;
  priority: number;
}

interface ProjectedLevels {
  startPoint: Partial<Point>;
  endPoint: Partial<Point>;
  firstPoint: Partial<Point>;
  secondPoint: Partial<Point>;
  range: number;
  levels: Array<{ level: FibLevel; price: number }>;
}

// ---------------------------------------------------------------------------
// CSS Token Helper
// ---------------------------------------------------------------------------

function getCssToken(token: string): string {
  const tokens: Record<string, string> = {
    '--accent-primary': '#0EC4B4',
    '--accent-focus': 'rgba(14,196,180,0.25)',
    '--border-strong': 'rgba(255,255,255,0.18)',
    '--edge-specular': '#8FA8C8',
    '--text-tertiary': '#8FA8C8',
    '--chart-overlay-text': '#E4EAF4',
    '--text-secondary': '#8FA8C8',
    '--chart-overlay-surface': 'rgba(8, 13, 23, 0.95)',
    '--accent-border-soft': 'rgba(14,196,180,0.38)',
    '--bullish': '#16B85A',
    '--bearish': '#F04545',
    '--bg-surface': '#0C1320',
    '--bg-overlay': '#182235',
  };
  return tokens[token] || '#E4EAF4';
}

// ---------------------------------------------------------------------------
// Canvas Utilities
// ---------------------------------------------------------------------------

let measureContext: CanvasRenderingContext2D | null = null;
let cssColorContext: CanvasRenderingContext2D | null = null;

function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContext) return measureContext;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  measureContext = canvas.getContext('2d');
  return measureContext;
}

function getCssColorContext(): CanvasRenderingContext2D | null {
  if (cssColorContext) return cssColorContext;
  if (typeof document === 'undefined') return null;
  const canvas = document.createElement('canvas');
  cssColorContext = canvas.getContext('2d');
  return cssColorContext;
}

function withAlpha(color: string, alpha: number): string {
  const context = getCssColorContext();
  if (!context) return color;

  context.fillStyle = '#000000';
  context.fillStyle = color;
  const normalized = context.fillStyle;

  if (normalized.startsWith('#') && normalized.length === 7) {
    const r = Number.parseInt(normalized.slice(1, 3), 16);
    const g = Number.parseInt(normalized.slice(3, 5), 16);
    const b = Number.parseInt(normalized.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const match = normalized.match(/^rgba?\(([^)]+)\)$/i);
  if (!match) return color;

  const channels = match[1].split(',').map((part) => Number.parseFloat(part.trim()));
  if (channels.length < 3 || channels.slice(0, 3).some((value) => !Number.isFinite(value))) {
    return color;
  }

  return `rgba(${channels[0]}, ${channels[1]}, ${channels[2]}, ${alpha})`;
}

function measureTextWidth(text: string | number | null | undefined): number {
  const context = getMeasureContext();
  if (!context) {
    return String(text ?? '').length * 7;
  }

  context.font = `${LABEL_FONT_WEIGHT} ${LABEL_FONT_SIZE_PX}px ${LABEL_FONT_FAMILY}`;
  return Math.ceil(context.measureText(String(text ?? '')).width);
}

// ---------------------------------------------------------------------------
// Math / Data Utilities
// ---------------------------------------------------------------------------

function toFiniteNumber(value: unknown): number | null {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) ? nextValue : null;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(value, min), max);
}

function isNear(value: number, target: number, epsilon = 0.0005): boolean {
  const left = toFiniteNumber(value);
  const right = toFiniteNumber(target);
  if (left == null || right == null) return false;
  return Math.abs(left - right) <= epsilon;
}

function createLevelId(value: number, index: number): string {
  const normalizedValue = String(value).replace(/[^0-9-]+/g, '_');
  return `fib-level-${index}-${normalizedValue}`;
}

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatCustomFibonacciLevelValue(value: number | null | undefined, maximumFractionDigits = 3): string {
  const finiteValue = toFiniteNumber(value);
  if (finiteValue == null) return '';

  const formatted = finiteValue.toFixed(Math.max(0, maximumFractionDigits));
  return formatted.replace(/\.?0+$/, '') || '0';
}

function formatPrice(value: number | null | undefined, precision = 2): string {
  const finiteValue = toFiniteNumber(value);
  if (finiteValue == null) return '--';
  return finiteValue.toFixed(Math.max(0, precision));
}

// ---------------------------------------------------------------------------
// Level / Snapshot Utilities
// ---------------------------------------------------------------------------

export function createDefaultCustomFibLevels(): FibLevel[] {
  return DEFAULT_CUSTOM_FIB_LEVEL_VALUES.map((value, index) => ({
    id: createLevelId(value, index),
    value,
    visible: true,
  }));
}

function normalizeLevel(level: unknown, index: number): FibLevel | null {
  const candidate = typeof level === 'number' ? { value: level } : (level as Record<string, unknown>);
  const value = toFiniteNumber(candidate?.value);
  if (value == null) return null;

  return {
    id: typeof candidate?.id === 'string' && candidate.id.length > 0
      ? candidate.id
      : createLevelId(value, index),
    value,
    visible: candidate?.visible !== false,
    color: typeof candidate?.color === 'string' && candidate.color.trim().length > 0
      ? candidate.color
      : undefined,
  };
}

export function normalizeCustomFibonacciExtendData(extendData: unknown): FibExtendData {
  const data = extendData as Record<string, unknown> | null | undefined;
  const levelSource = Array.isArray(data?.levels) && data.levels.length > 0
    ? data.levels
    : createDefaultCustomFibLevels();
  const levels = (levelSource as unknown[])
    .map((level, index) => normalizeLevel(level, index))
    .filter((l): l is FibLevel => l !== null);

  return {
    levels: levels.length > 0 ? levels : createDefaultCustomFibLevels(),
    reverse: Boolean(data?.reverse),
    extendLeft: Boolean(data?.extendLeft),
    extendRight: Boolean(data?.extendRight),
    trendLine: {
      visible: (data?.trendLine as any)?.visible !== false,
      color: typeof (data?.trendLine as any)?.color === 'string' ? (data?.trendLine as any).color : undefined,
      size: toFiniteNumber((data?.trendLine as any)?.size) ?? undefined,
      style: ['solid', 'dashed', 'dotted'].includes((data?.trendLine as any)?.style) ? (data?.trendLine as any).style : undefined,
    },
    backgrounds: {
      visible: (data?.backgrounds as any)?.visible !== false,
      alpha: toFiniteNumber((data?.backgrounds as any)?.alpha) ?? 0.08,
    },
    labels: {
      showPrices: (data?.labels as any)?.showPrices !== false,
      showLevels: (data?.labels as any)?.showLevels !== false,
      levelsFormat: (data?.labels as any)?.levelsFormat === 'percents' ? 'percents' : 'values',
      position: ['left', 'center', 'right'].includes((data?.labels as any)?.position) ? (data?.labels as any).position : 'left',
      align: ['top', 'middle', 'bottom'].includes((data?.labels as any)?.align) ? (data?.labels as any).align : 'middle',
    },
    fibBasedOnLogScale: Boolean(data?.fibBasedOnLogScale),
    goldenZone: {
      from: toFiniteNumber((data?.goldenZone as Record<string, unknown>)?.from) ?? DEFAULT_GOLDEN_ZONE.from,
      to: toFiniteNumber((data?.goldenZone as Record<string, unknown>)?.to) ?? DEFAULT_GOLDEN_ZONE.to,
      visible: (data?.goldenZone as Record<string, unknown>)?.visible !== false,
    },
    lytickStyleVariant: data?.lytickStyleVariant === 'amber' ? 'amber' : 'default',
  };
}

export function normalizeCustomFibonacciSnapshot(snapshot: FibSnapshot | unknown): unknown {
  if (!snapshot || typeof snapshot !== 'object') return snapshot;

  const snap = snapshot as FibSnapshot;
  const shouldNormalize = (
    snap.name === CUSTOM_FIB_OVERLAY_NAME
    || LEGACY_CUSTOM_FIB_OVERLAY_NAMES.includes(snap.name ?? '')
  );
  if (!shouldNormalize) return snapshot;

  return {
    ...snapshot,
    name: CUSTOM_FIB_OVERLAY_NAME,
    extendData: normalizeCustomFibonacciExtendData(snap.extendData),
  };
}

// ---------------------------------------------------------------------------
// Overlay Point Helpers
// ---------------------------------------------------------------------------

function copyPerformPoint(
  target: Partial<Point> | undefined,
  source: Partial<Point> | undefined,
): void {
  if (!target || !source) return;

  const nextTimestamp = toFiniteNumber(source.timestamp);
  const nextDataIndex = toFiniteNumber(source.dataIndex);
  const nextValue = toFiniteNumber(source.value);

  if (nextTimestamp != null) {
    target.timestamp = nextTimestamp;
  }
  if (nextDataIndex != null) {
    target.dataIndex = nextDataIndex;
  }
  if (nextValue != null) {
    target.value = nextValue;
  }
}

// ---------------------------------------------------------------------------
// Palette Helpers
// ---------------------------------------------------------------------------

function getDefaultPalette(): Palette {
  const accent = getCssToken('--accent-primary');
  return {
    accent,
    accentGhost: getCssToken('--accent-focus'),
    baseTrend: accent,
    lineColor: accent,
    levelLine: getCssToken('--border-strong'),
    levelLineStrong: getCssToken('--edge-specular'),
    extensionLine: getCssToken('--text-tertiary'),
    zoneFill: withAlpha(accent, 0.08),
    zoneBorder: getCssToken('--accent-border-soft'),
    textPrimary: getCssToken('--chart-overlay-text'),
    textSecondary: getCssToken('--text-secondary'),
    labelBackground: getCssToken('--chart-overlay-surface'),
    labelBorder: getCssToken('--border-strong'),
    pointBorderColor: accent,
    activeBorderColor: accent,
    activeColor: getCssToken('--accent-focus'),
  };
}

function getPalette(overlay: Overlay<FibExtendData>): Palette {
  const defaults = getDefaultPalette();

  const styles = overlay.styles as Record<string, Record<string, unknown>> | null | undefined;
  const lineColor = (styles?.line?.color as string) ?? defaults.accent;
  const pointBorderColor = (styles?.point?.borderColor as string) ?? lineColor;
  const activeBorderColor = (styles?.point?.activeBorderColor as string) ?? pointBorderColor;
  const activeColor = (styles?.point?.activeColor as string) ?? defaults.accentGhost;
  const zoneFill = (styles?.rect?.color as string) ?? defaults.zoneFill;
  const zoneBorder = (styles?.rect?.borderColor as string) ?? lineColor;
  const levelLine = (styles?.polygon?.borderColor as string) ?? defaults.levelLine;

  return {
    ...defaults,
    lineColor,
    pointBorderColor,
    activeBorderColor,
    activeColor,
    zoneFill,
    zoneBorder,
    levelLine,
  };
}

// ---------------------------------------------------------------------------
// Text Styles
// ---------------------------------------------------------------------------

function getTextStyles(
  color: string,
  options: { size?: number; weight?: number } = {},
): Record<string, unknown> {
  const { size = LABEL_FONT_SIZE_PX, weight = LABEL_FONT_WEIGHT } = options;
  return {
    style: 'fill',
    color,
    size,
    family: LABEL_FONT_FAMILY,
    weight,
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    borderSize: 0,
    borderStyle: 'solid',
    borderRadius: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    paddingBottom: 0,
  };
}

// ---------------------------------------------------------------------------
// Level Priority & Label Picking
// ---------------------------------------------------------------------------

function getLevelPriority(levelValue: number): number {
  if (isNear(levelValue, 0) || isNear(levelValue, 1)) return 100;
  if (isNear(levelValue, 0.5) || isNear(levelValue, 0.618)) return 95;
  if (isNear(levelValue, 0.382) || isNear(levelValue, 0.786)) return 85;
  if (levelValue < 0 || levelValue > 1) return 65;
  return 75;
}

function shouldUseCompactLabels(bounding: Bounding): boolean {
  const width = Number(bounding?.width) || 0;
  if (typeof window === 'undefined') {
    return width > 0 && width <= 767;
  }

  return Math.min(window.innerWidth || width, width || window.innerWidth || 0) <= 767;
}

function pickLabelEntries(entries: LabelEntry[], minimumGap: number): LabelEntry[] {
  const chosen: LabelEntry[] = [];

  [...entries]
    .sort((left, right) => (
      right.priority - left.priority
      || left.y - right.y
    ))
    .forEach((entry) => {
      const overlaps = chosen.some((candidate) => Math.abs(candidate.y - entry.y) < minimumGap);
      if (!overlaps) {
        chosen.push(entry);
      }
    });

  return chosen.sort((left, right) => left.y - right.y);
}

// ---------------------------------------------------------------------------
// Label Figure Builder
// ---------------------------------------------------------------------------

function buildLabelFigures({
  entries,
  lineStartX,
  lineEndX,
  bounding,
  precision,
  compact,
  palette,
  extendData,
}: {
  entries: LabelEntry[];
  lineStartX: number;
  lineEndX: number;
  bounding: Bounding;
  precision: number;
  compact: boolean;
  palette: Palette;
  extendData: FibExtendData;
}): OverlayFigure[] {
  if (entries.length === 0) return [];

  const chartWidth = Math.max(0, Number(bounding?.width) || 0);

  return entries.flatMap((entry): OverlayFigure[] => {
    let ratioStr = '';
    if (extendData.labels.levelsFormat === 'percents') {
      ratioStr = formatCustomFibonacciLevelValue(entry.level.value * 100) + '%';
    } else {
      ratioStr = formatCustomFibonacciLevelValue(entry.level.value);
    }
    const priceStr = formatPrice(entry.price, precision);
    
    let text = '';
    if (extendData.labels.showLevels && extendData.labels.showPrices) {
      if (compact) text = ratioStr;
      else text = `${ratioStr}  (${priceStr})`;
    } else if (extendData.labels.showLevels) {
      text = ratioStr;
    } else if (extendData.labels.showPrices) {
      text = priceStr;
    } else {
      return [];
    }

    const textWidth = measureTextWidth(text);
    const labelWidth = textWidth + (LABEL_HORIZONTAL_PADDING_PX * 2);
    
    let leftX = 0;
    const padding = LABEL_END_GAP_PX;

    if (extendData.labels.position === 'left') {
      leftX = lineStartX + padding;
      leftX = clamp(leftX, LABEL_EDGE_PADDING_PX, Math.max(LABEL_EDGE_PADDING_PX, chartWidth - labelWidth - LABEL_EDGE_PADDING_PX));
    } else if (extendData.labels.position === 'center') {
      const midX = (lineStartX + lineEndX) / 2;
      leftX = midX - (labelWidth / 2);
    } else {
      leftX = lineEndX - labelWidth - padding;
      leftX = clamp(leftX, LABEL_EDGE_PADDING_PX, Math.max(LABEL_EDGE_PADDING_PX, chartWidth - labelWidth - LABEL_EDGE_PADDING_PX));
    }

    let topY = 0;
    if (extendData.labels.align === 'bottom') {
      topY = entry.y;
    } else if (extendData.labels.align === 'top') {
      topY = entry.y - LABEL_HEIGHT_PX;
    } else {
      topY = entry.y - (LABEL_HEIGHT_PX / 2);
    }

    return [
      {
        type: 'rect',
        attrs: {
          x: leftX,
          y: topY,
          width: labelWidth,
          height: LABEL_HEIGHT_PX,
        },
        styles: {
          style: 'stroke_fill',
          color: palette.labelBackground,
          borderColor: palette.labelBorder,
          borderSize: 1,
          borderStyle: 'solid',
          borderRadius: 999,
        },
        ignoreEvent: true,
      },
      {
        type: 'text',
        attrs: {
          x: leftX + LABEL_HORIZONTAL_PADDING_PX,
          y: topY + LABEL_VERTICAL_PADDING_PX + (LABEL_FONT_SIZE_PX / 2) + 1,
          text,
          align: 'left',
          baseline: 'middle',
        },
        styles: getTextStyles(compact ? palette.textPrimary : palette.textSecondary),
        ignoreEvent: true,
      },
    ];
  });
}

export function calculateFibPrice(
  startValue: number,
  endValue: number,
  levelValue: number,
  isLogScale: boolean
): number {
  if (isLogScale && startValue > 0 && endValue > 0) {
    const logStart = Math.log10(startValue);
    const logEnd = Math.log10(endValue);
    return Math.pow(10, logStart + (logEnd - logStart) * levelValue);
  }
  return startValue + (endValue - startValue) * levelValue;
}

// ---------------------------------------------------------------------------
// Level Projection
// ---------------------------------------------------------------------------

function projectLevels(
  points: Array<Partial<Point>>,
  extendData: FibExtendData,
): ProjectedLevels | null {
  if (!Array.isArray(points) || points.length < 2) return null;

  const firstPoint = points[0] ?? {};
  const secondPoint = points[1] ?? {};
  const startPoint = extendData.reverse ? secondPoint : firstPoint;
  const endPoint = extendData.reverse ? firstPoint : secondPoint;
  const startValue = toFiniteNumber(startPoint?.value);
  const endValue = toFiniteNumber(endPoint?.value);
  if (startValue == null || endValue == null) return null;

  const range = endValue - startValue;
  const visibleLevels = extendData.levels.filter((level) => level.visible !== false);

  return {
    startPoint,
    endPoint,
    firstPoint,
    secondPoint,
    range,
    levels: visibleLevels.map((level) => ({
      level,
      price: calculateFibPrice(startValue, endValue, level.value, extendData.fibBasedOnLogScale),
    })),
  };
}

// ---------------------------------------------------------------------------
// Coordinate Helpers
// ---------------------------------------------------------------------------

function toCoordinateMap(
  keys: string[],
  coordinates: Array<Partial<Coordinate>>,
): Record<string, Partial<Coordinate>> {
  return keys.reduce<Record<string, Partial<Coordinate>>>((accumulator, key, index) => ({
    ...accumulator,
    [key]: coordinates[index] ?? null,
  }), {});
}

// ---------------------------------------------------------------------------
// Overlay Template Factory
// ---------------------------------------------------------------------------

export function createCustomFibonacciOverlayTemplate(
  getCssTokenFn: (token: string) => string,
): OverlayTemplate<FibExtendData> {
  // We pass getCssTokenFn into the closure; getCssToken is used by palette helpers
  // but we keep the module-level getCssToken for simplicity.
  void getCssTokenFn;

  return {
    name: CUSTOM_FIB_OVERLAY_NAME,
    totalStep: 3,
    needDefaultPointFigure: true,
    needDefaultXAxisFigure: false,
    needDefaultYAxisFigure: false,
    onRightClick: (e) => {
      e?.preventDefault?.();
      return false; // Yield selection control to instance-level bridge handler
    },
    styles: {
      line: {
        color: getCssToken('--accent-primary'),
        size: 1.2,
      },
      point: {
        radius: 6,
        activeRadius: 10,
        borderSize: 1.5,
        activeBorderSize: 2,
      },
      rect: {
        color: withAlpha(getCssToken('--accent-primary'), 0.08),
        borderColor: getCssToken('--accent-primary'),
      },
      polygon: {
        borderColor: getCssToken('--border-strong'),
      },
    },
    createPointFigures: ({
      chart,
      overlay,
      coordinates,
      bounding,
    }: {
      chart: Chart;
      overlay: Overlay<FibExtendData>;
      coordinates: Array<Partial<Coordinate>>;
      bounding: Bounding;
    }): OverlayFigure[] => {
      if (coordinates.length < 2) return [];

      const extendData = normalizeCustomFibonacciExtendData(overlay.extendData);
      const palette = getPalette(overlay);
      
      const baseLineSize = typeof overlay.styles?.line?.size === 'number' ? overlay.styles.line.size : 1.2;
      const baseLineStyle = typeof overlay.styles?.line?.style === 'string' ? overlay.styles.line.style : 'solid';

      const projected = projectLevels(overlay.points, extendData);
      if (!projected) return [];

      const firstCoordinate = coordinates[0];
      const secondCoordinate = coordinates[1];
      const lineStartX = extendData.extendLeft
        ? 0
        : Math.min(Number(firstCoordinate?.x), Number(secondCoordinate?.x));
      const lineEndX = extendData.extendRight
        ? Math.max(0, Number(bounding?.width) || 0)
        : Math.max(Number(firstCoordinate?.x), Number(secondCoordinate?.x));

      const startValue = toFiniteNumber(projected.startPoint?.value) ?? 0;

      const pointsToProject: Array<Partial<Point>> = projected.levels.map(({ price }) => ({
        ...projected.startPoint,
        value: price,
      }));

      const projectedCoordinatesRaw = chart.convertToPixel?.(pointsToProject, {
        paneId: overlay.paneId,
      });
      const projectedCoordinates = Array.isArray(projectedCoordinatesRaw)
        ? projectedCoordinatesRaw
        : [projectedCoordinatesRaw];

      const levelEntries: LabelEntry[] = projected.levels
        .map(({ level, price }, index): LabelEntry | null => {
          const coordinate = projectedCoordinates[index];
          const y = Number(coordinate?.y);
          if (!Number.isFinite(y)) return null;

          return {
            level,
            price,
            y,
            priority: getLevelPriority(level.value),
          };
        })
        .filter((entry): entry is LabelEntry => entry !== null);

      const compact = shouldUseCompactLabels(bounding);
      const minimumGap = compact ? LABEL_MIN_GAP_MOBILE_PX : LABEL_MIN_GAP_DESKTOP_PX;
      const labelEntries = pickLabelEntries(levelEntries, minimumGap);
      const pricePrecision = Math.max(0, Number(chart.getSymbol?.()?.pricePrecision) || 2);

      const lineFigures: OverlayFigure[] = levelEntries.map((entry): OverlayFigure => {
        const isExtension = entry.level.value < 0 || entry.level.value > 1;
        const isMajor = (
          isNear(entry.level.value, 0)
          || isNear(entry.level.value, 1)
          || isNear(entry.level.value, 0.5)
          || isNear(entry.level.value, 0.618)
        );

        return {
          type: 'line',
          attrs: {
            coordinates: [
              { x: lineStartX, y: entry.y },
              { x: lineEndX, y: entry.y },
            ],
          },
          styles: {
            color: entry.level.color
              ? entry.level.color
              : isMajor
                ? palette.lineColor
                : isExtension
                  ? palette.extensionLine
                  : palette.levelLine,
            size: isMajor ? baseLineSize : Math.max(1, baseLineSize - 0.5),
            style: isExtension ? 'dashed' : (baseLineStyle as any),
            dashedValue: isExtension ? [6, 4] : baseLineStyle === 'dashed' ? [6, 4] : undefined,
          },
        };
      });

      const sortedEntriesForBackground = [...levelEntries].sort((a, b) => a.y - b.y);
      const zoneFigures: OverlayFigure[] = [];

      if (extendData.backgrounds.visible && sortedEntriesForBackground.length >= 2) {
        for (let i = 0; i < sortedEntriesForBackground.length - 1; i++) {
          const topEntry = sortedEntriesForBackground[i];
          const bottomEntry = sortedEntriesForBackground[i + 1];
          const height = bottomEntry.y - topEntry.y;
          if (height <= 0) continue;

          const isMajor = isNear(topEntry.level.value, 0) || isNear(topEntry.level.value, 1) || isNear(topEntry.level.value, 0.5) || isNear(topEntry.level.value, 0.618);
          const fillBase = topEntry.level.color 
            ? topEntry.level.color 
            : isMajor ? palette.lineColor : palette.levelLine;

          const fillColor = withAlpha(fillBase, extendData.backgrounds.alpha);
          
          zoneFigures.push({
            type: 'rect',
            attrs: {
              x: lineStartX,
              y: topEntry.y,
              width: Math.max(1, lineEndX - lineStartX),
              height: height,
            },
            styles: {
              style: 'fill',
              color: fillColor,
            },
            ignoreEvent: true,
          });
        }
      }

      let trendLineFigures: OverlayFigure[] = [];
      if (extendData.trendLine.visible) {
        trendLineFigures = [{
          type: 'line',
          attrs: {
            coordinates: [
              { x: Number(firstCoordinate?.x), y: Number(firstCoordinate?.y) },
              { x: Number(secondCoordinate?.x), y: Number(secondCoordinate?.y) },
            ],
          },
          styles: {
            color: extendData.trendLine.color || palette.lineColor,
            size: extendData.trendLine.size || baseLineSize,
            style: extendData.trendLine.style || baseLineStyle,
            dashedValue: (extendData.trendLine.style || baseLineStyle) === 'dashed' ? [6, 4] : (extendData.trendLine.style || baseLineStyle) === 'dotted' ? [2, 2] : undefined,
          },
        }];
      }

      const labelFigures = buildLabelFigures({
        entries: labelEntries,
        lineStartX,
        lineEndX,
        bounding,
        precision: pricePrecision,
        compact,
        palette,
        extendData,
      });

      return [
        ...zoneFigures,
        ...lineFigures,
        ...trendLineFigures,
        ...labelFigures,
      ];
    },
    createXAxisFigures() {
      return [];
    },
    createYAxisFigures() {
      return [];
    },
    performEventMoveForDrawing({
      currentStep,
      performPointIndex,
      points,
      performPoint,
    }: OverlayPerformEventParams): void {
      if (currentStep === 2 && performPointIndex === 1 && Array.isArray(points)) {
        const nextPoint = { ...(points[1] ?? {}) };
        copyPerformPoint(nextPoint, performPoint);
        points[1] = nextPoint;
      }
    },
    onMouseEnter({ overlay }: OverlayEvent<FibExtendData>): void {
      (overlay as unknown as Record<string, unknown>)._customFibHovered = true;
    },
    onMouseLeave({ overlay }: OverlayEvent<FibExtendData>): void {
      (overlay as unknown as Record<string, unknown>)._customFibHovered = false;
    },
    onSelected({ overlay }: OverlayEvent<FibExtendData>): void {
      (overlay as unknown as Record<string, unknown>)._customFibSelected = true;
    },
    onDeselected({ overlay }: OverlayEvent<FibExtendData>): void {
      (overlay as unknown as Record<string, unknown>)._customFibSelected = false;
      (overlay as unknown as Record<string, unknown>)._customFibHovered = false;
    },
  };
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export async function registerCustomFibonacciOverlay(): Promise<void> {
  const { registerOverlay } = await import('klinecharts');
  registerOverlay(createCustomFibonacciOverlayTemplate(getCssToken));
}
